/* =============================================================
   AED Professionals — State AED Law Checker
   Theme asset: assets/aed-law-checker.js
   -------------------------------------------------------------
   Deploys exactly like the other tools:
   1) Add this file to /assets.
   2) layout/theme.liquid, ONE line above </body>:
        <script src="{{ 'aed-law-checker.js' | asset_url }}" defer></script>
   3) Drop aed-law-checker-shopify-embed.html into a GemPages
      Liquid/Custom HTML element on the law-checker page.
   No product links, no data dependencies — a clean drop-in.
   -------------------------------------------------------------
   IMPORTANT — this tool provides general educational information,
   not legal advice. AED statutes change and vary widely. Each
   state card routes the user to the matching AED Professionals
   state compliance page for deeper, on-site guidance.
   Framework grounded in the federal Cardiac Arrest Survival Act
   (42 U.S.C. 238q) and confirmed that all 50 states + DC have AED
   Good Samaritan immunity laws (reviewed 2026-06).
   ============================================================= */
(function () {
  "use strict";

  /* immunity types: B = Broad, C = Conditional (immunity tied to program
     compliance), S = Standard. All jurisdictions provide good-faith civil
     immunity for AED use; the type describes how that protection is scoped. */
  var STATES = [
    { n: "Alabama", a: "AL", t: "S" },
    { n: "Alaska", a: "AK", t: "S" },
    { n: "Arizona", a: "AZ", t: "S" },
    { n: "Arkansas", a: "AR", t: "S" },
    { n: "California", a: "CA", t: "S", note: "Detailed program rules for AED acquirers under Health & Safety Code §1797.196 — maintenance/testing per manufacturer, responder training, EMS notification, and signage. Building-occupancy AED requirements also apply to certain new construction." },
    { n: "Colorado", a: "CO", t: "S" },
    { n: "Connecticut", a: "CT", t: "S" },
    { n: "Delaware", a: "DE", t: "S" },
    { n: "District of Columbia", a: "DC", t: "S" },
    { n: "Florida", a: "FL", t: "B", note: "Broad immunity under the Cardiac Arrest Survival Act provisions (Fla. Stat. §768.1325); businesses are encouraged but not required to have AEDs. Intentional tampering with an AED or falsifying AED records is a first-degree misdemeanor (§401.2915)." },
    { n: "Georgia", a: "GA", t: "S" },
    { n: "Hawaii", a: "HI", t: "S" },
    { n: "Idaho", a: "ID", t: "S" },
    { n: "Illinois", a: "IL", t: "S", note: "The Automated External Defibrillator Act and Physical Fitness Facility Medical Emergency Preparedness Act set requirements for certain facilities, including trained AED users at fitness facilities." },
    { n: "Indiana", a: "IN", t: "S" },
    { n: "Iowa", a: "IA", t: "S" },
    { n: "Kansas", a: "KS", t: "S" },
    { n: "Kentucky", a: "KY", t: "S" },
    { n: "Louisiana", a: "LA", t: "S" },
    { n: "Maine", a: "ME", t: "S" },
    { n: "Maryland", a: "MD", t: "C", note: "Conditional immunity: AED program immunity is tied to compliance with state requirements, including registration with the state EMS authority (MIEMSS). A lapsed registration can cost you liability protection." },
    { n: "Massachusetts", a: "MA", t: "S", note: "AED requirements focus heavily on health and fitness facilities (M.G.L. Ch. 93 §78A and related provisions)." },
    { n: "Michigan", a: "MI", t: "S" },
    { n: "Minnesota", a: "MN", t: "S", note: "Has a limited duty-to-assist provision; Good Samaritan AED immunity still applies for good-faith use." },
    { n: "Mississippi", a: "MS", t: "S" },
    { n: "Missouri", a: "MO", t: "S" },
    { n: "Montana", a: "MT", t: "S" },
    { n: "Nebraska", a: "NE", t: "S" },
    { n: "Nevada", a: "NV", t: "S" },
    { n: "New Hampshire", a: "NH", t: "S" },
    { n: "New Jersey", a: "NJ", t: "C", note: "Conditional immunity: Good Samaritan protection for AED program owners is contingent on meeting administrative requirements (registration, training, physician oversight, maintenance). Health clubs that fail AED requirements can face penalties." },
    { n: "New Mexico", a: "NM", t: "S" },
    { n: "New York", a: "NY", t: "S", note: "Public Access Defibrillation governed by Public Health Law §3000-b (program/collaborative agreement) and §3000-a (Good Samaritan). Schools and certain public facilities have AED placement rules." },
    { n: "North Carolina", a: "NC", t: "S" },
    { n: "North Dakota", a: "ND", t: "S" },
    { n: "Ohio", a: "OH", t: "S" },
    { n: "Oklahoma", a: "OK", t: "S" },
    { n: "Oregon", a: "OR", t: "B", note: "Broad immunity extending to rescuers, purchasers, property owners, physicians, and trainers; requirements strengthened for larger public settings and schools." },
    { n: "Pennsylvania", a: "PA", t: "S" },
    { n: "Rhode Island", a: "RI", t: "S", note: "Has a limited duty-to-assist provision; Good Samaritan AED immunity still applies for good-faith use." },
    { n: "South Carolina", a: "SC", t: "S" },
    { n: "South Dakota", a: "SD", t: "S" },
    { n: "Tennessee", a: "TN", t: "S" },
    { n: "Texas", a: "TX", t: "S", note: "Broad lay-rescuer immunity for good-faith AED use; AED program provisions appear in the Health & Safety Code (Ch. 779)." },
    { n: "Utah", a: "UT", t: "S" },
    { n: "Vermont", a: "VT", t: "S", note: "Has a limited duty-to-assist provision; Good Samaritan AED immunity still applies for good-faith use." },
    { n: "Virginia", a: "VA", t: "B", note: "Lighter regulatory approach focused primarily on liability protection; broad immunity for good-faith use." },
    { n: "Washington", a: "WA", t: "B", note: "Requires a structured AED program for entities that acquire a defibrillator, paired with one of the broader Good Samaritan frameworks (RCW 4.24.300). Immunity excludes gross negligence / willful misconduct." },
    { n: "West Virginia", a: "WV", t: "S" },
    { n: "Wisconsin", a: "WI", t: "S" },
    { n: "Wyoming", a: "WY", t: "S" }
  ];

  var TYPE = {
    B: { label: "Broad immunity", cls: "alc-badge-broad",
         desc: "Protection extends widely (often to rescuers, purchasers, property owners, physicians, and trainers) for good-faith use, without strict program preconditions." },
    C: { label: "Conditional immunity", cls: "alc-badge-cond",
         desc: "Liability protection for the AED program owner is tied to meeting state requirements (e.g., registration, training, oversight, maintenance). Falling out of compliance can cost you immunity." },
    S: { label: "Good Samaritan immunity", cls: "alc-badge-std",
         desc: "Good-faith use of an AED in a perceived emergency is shielded from civil liability, subject to the universal exceptions below." }
  };

  /* Program-owner readiness checklist — national best practice plus the
     obligations states most commonly impose. Confirm specifics with your state. */
  var CHECKLIST = [
    ["Medical direction", "Many states expect physician/medical oversight of your AED program. Confirm whether yours is required or recommended."],
    ["Responder training", "Have expected responders trained in CPR/AED to a recognized standard (e.g., AHA, Red Cross)."],
    ["Maintenance & readiness", "Maintain and test per the manufacturer's guidelines; keep pads and battery in date. The federal acquirer immunity hinges on proper maintenance."],
    ["EMS notification / registration", "Notify local EMS of the AED's placement; some states (e.g., NJ, MD) require formal registration to preserve immunity."],
    ["Post-use reporting", "Some states require notifying EMS or filing a report after an AED is used. Confirm your state's rule."]
  ];

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  /* Route to the matching AED Professionals state compliance page.
     State names map 1:1 to the 50-State Guide slug pattern:
     "Illinois" -> /pages/illinois-aed-laws
     "District of Columbia" -> /pages/district-of-columbia-aed-laws */
  function sourceUrl(state) {
    var slug = state.n.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    return "/pages/" + slug + "-aed-laws";
  }

  function render(root, state) {
    var out = root.querySelector("[data-alc-results]");
    out.innerHTML = "";
    if (!state) {
      out.appendChild(el("p", "alc-empty", "Select your state to see how its AED Good Samaritan law is structured, plus a compliance checklist for AED program owners."));
      return;
    }

    var ty = TYPE[state.t];

    var head = el("div", "alc-result-head");
    head.appendChild(el("h3", null, state.n));
    head.appendChild(el("span", "alc-badge " + ty.cls, ty.label));
    out.appendChild(head);

    out.appendChild(el("p", "alc-type-desc", ty.desc));

    if (state.note) {
      var n = el("div", "alc-statenote");
      n.appendChild(el("span", "alc-statenote-label", "State-specific note"));
      n.appendChild(el("p", null, state.note));
      out.appendChild(n);
    }

    /* Universal facts */
    var uni = el("div", "alc-card");
    uni.appendChild(el("h4", null, "What's true everywhere"));
    var ul = el("ul", "alc-list");
    ul.appendChild(el("li", null, "All 50 states and DC provide civil immunity for good-faith AED use in a perceived emergency."));
    ul.appendChild(el("li", null, "The federal <strong>Cardiac Arrest Survival Act (42 U.S.C. §238q)</strong> sets a baseline of protection for users and acquirers nationwide."));
    ul.appendChild(el("li", null, "Protection requires <strong>good faith</strong> and does <strong>not</strong> cover gross negligence or willful/wanton misconduct."));
    uni.appendChild(ul);
    out.appendChild(uni);

    /* Program owner checklist */
    var chk = el("div", "alc-card");
    chk.appendChild(el("h4", null, "AED program owner checklist"));
    chk.appendChild(el("p", "alc-card-sub", "Common obligations for organizations that own an AED. Requirements vary by state — confirm yours below."));
    var grid = el("div", "alc-check-grid");
    CHECKLIST.forEach(function (c) {
      var item = el("div", "alc-check");
      item.appendChild(el("div", "alc-check-title", c[0]));
      item.appendChild(el("div", "alc-check-body", c[1]));
      grid.appendChild(item);
    });
    chk.appendChild(grid);
    out.appendChild(chk);

    /* Verify CTA */
    var cta = el("div", "alc-verify");
    cta.appendChild(el("p", null, "<strong>Confirm " + state.n + "'s current rules.</strong> Statutes change. Review the full requirements on our state compliance page, and verify with your state EMS authority or counsel before relying on this summary."));
    var btnRow = el("div", "alc-btn-row");
    var src = el("a", "alc-btn", "Get More Information");
    src.href = sourceUrl(state); src.setAttribute("rel", "noopener");
    btnRow.appendChild(src);
    var fed = el("a", "alc-btn alc-btn-ghost", "Read the Federal Act");
    fed.href = "https://www.congress.gov/bill/119th-congress/house-bill/1466/text"; fed.target = "_blank"; fed.setAttribute("rel", "noopener");
    btnRow.appendChild(fed);
    cta.appendChild(btnRow);
    out.appendChild(cta);
  }

  function init() {
    var root = document.getElementById("aed-law-checker");
    if (!root) return; /* dormant on every other page */

    var sel = root.querySelector("[data-alc-state]");
    if (!sel) return;

    STATES.forEach(function (s, i) {
      var o = document.createElement("option");
      o.value = i; o.textContent = s.n; sel.appendChild(o);
    });

    sel.addEventListener("change", function () {
      render(root, sel.value !== "" ? STATES[+sel.value] : null);
    });

    render(root, null);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();